import { transport } from './client';
import { arrayOf, bool, int, nullableStr, object, record, str, anyBody, optional } from './decode';
import { decodeSessionUser, decodeLoginResult } from './types';
import { sourceDecoder } from './pool';

export const tenantDecoder = object({ id: str, name: str, status: str });
export type PlatformTenant = ReturnType<typeof tenantDecoder>;
export const platformUserDecoder = object({
  id: str,
  email: str,
  display_name: str,
  status: str,
  is_platform_operator: bool,
  must_change_password: bool,
});
export type PlatformUser = ReturnType<typeof platformUserDecoder>;
const listTenants = object({ items: arrayOf(tenantDecoder), total: int });
const listUsers = object({ items: arrayOf(platformUserDecoder), total: int });
export const fetchMe = (signal?: AbortSignal) =>
  transport.request('/auth/me', decodeSessionUser, {
    withTenant: false,
    ...(signal ? { signal } : {}),
  });
/**
 * A wrong current password answers 401; `anonymous` keeps the transport from
 * treating it as an expired session (which would sign the user out). An expired
 * session answers the same bare 401, so the caller probes `fetchMe` to tell them apart.
 */
export const changePassword = (current_password: string, new_password: string) =>
  transport.request('/auth/password', decodeLoginResult, {
    method: 'POST',
    withTenant: false,
    anonymous: true,
    body: { current_password, new_password },
  });
export const fetchPlatformTenants = (q = '', offset = 0, signal?: AbortSignal, status = '') =>
  transport.request('/ops/tenants', listTenants, {
    withTenant: false,
    query: { q, offset, ...(status ? { status } : {}) },
    ...(signal ? { signal } : {}),
  });
export const fetchPlatformTenant = (id: string, signal?: AbortSignal) =>
  transport.request(`/ops/tenants/${encodeURIComponent(id)}`, tenantDecoder, {
    withTenant: false,
    ...(signal ? { signal } : {}),
  });
export interface PlatformUserFilters {
  status?: string;
  is_platform_operator?: boolean;
}
export const fetchPlatformUsers = (
  q = '',
  offset = 0,
  signal?: AbortSignal,
  filters: PlatformUserFilters = {},
) =>
  transport.request('/ops/users', listUsers, {
    withTenant: false,
    query: { q, offset, ...filters },
    ...(signal ? { signal } : {}),
  });
export const fetchPlatformUser = (id: string, signal?: AbortSignal) =>
  transport.request(`/ops/users/${encodeURIComponent(id)}`, platformUserDecoder, {
    withTenant: false,
    ...(signal ? { signal } : {}),
  });
export const fetchUserMemberships = (id: string, signal?: AbortSignal) =>
  transport.request(
    `/ops/users/${encodeURIComponent(id)}/memberships`,
    object({
      items: arrayOf(
        object({ tenant_id: str, tenant_name: str, tenant_status: str, role: str, status: str }),
      ),
    }),
    { withTenant: false, ...(signal ? { signal } : {}) },
  );
export const createPlatformUser = (body: {
  email: string;
  display_name: string;
  is_platform_operator: boolean;
}) =>
  transport.request('/ops/users', object({ user: platformUserDecoder, initial_password: str }), {
    method: 'POST',
    withTenant: false,
    body,
  });
export const updatePlatformUser = (
  id: string,
  body: { status?: string; is_platform_operator?: boolean; display_name?: string },
) =>
  transport.request(`/ops/users/${encodeURIComponent(id)}`, platformUserDecoder, {
    method: 'PATCH',
    withTenant: false,
    body,
  });
export const resetPlatformPassword = (id: string) =>
  transport.request(
    `/ops/users/${encodeURIComponent(id)}/reset-password`,
    object({ initial_password: str }),
    { method: 'POST', withTenant: false },
  );
export const createPlatformTenant = (body: {
  name: string;
  owner_user_id?: string;
  owner?: { email: string; display_name: string };
}) =>
  transport.request(
    '/ops/tenants',
    object({ tenant: tenantDecoder, initial_password: nullableStr }),
    { method: 'POST', withTenant: false, body },
  );
export const updatePlatformTenant = (id: string, body: { name?: string; status?: string }) =>
  transport.request(`/ops/tenants/${encodeURIComponent(id)}`, tenantDecoder, {
    method: 'PATCH',
    withTenant: false,
    body,
  });
export const fetchMembers = (id: string, signal?: AbortSignal) =>
  transport.request(
    `/ops/tenants/${encodeURIComponent(id)}/members`,
    object({
      items: arrayOf(
        object({ user_id: str, email: str, display_name: str, role: str, status: str }),
      ),
    }),
    { withTenant: false, ...(signal ? { signal } : {}) },
  );
export const setMember = (tenant: string, user: string, role: string, status: string) =>
  transport.request(
    `/ops/tenants/${encodeURIComponent(tenant)}/members/${encodeURIComponent(user)}`,
    object({ role: str, status: str }),
    { method: 'PUT', withTenant: false, body: { role, status } },
  );
export const fetchOverview = (signal?: AbortSignal) =>
  transport.request(
    '/ops/overview',
    object({
      tenants: int,
      users: int,
      platform_administrators: int,
      suspended_tenants: optional(int, 0),
      disabled_users: optional(int, 0),
      password_changes_required: optional(int, 0),
    }),
    { withTenant: false, ...(signal ? { signal } : {}) },
  );
export const fetchRuntime = (signal?: AbortSignal) =>
  transport.request(
    '/ops/runtime',
    object({
      api: str,
      database: str,
      jobs: record(int),
      sources: record(int),
      recent_failures: optional(
        arrayOf(
          object({
            id: str,
            tenant_id: str,
            tenant_name: str,
            kind: str,
            status: str,
            attempt_count: int,
            updated_at: str,
            is_public_pool: bool,
          }),
        ),
        [],
      ),
    }),
    { withTenant: false, ...(signal ? { signal } : {}) },
  );
export interface PlatformAuditFilters {
  q?: string;
  outcome?: string;
  tenant_id?: string;
  actor_user_id?: string;
}
export const fetchAudit = (offset = 0, signal?: AbortSignal, filters: PlatformAuditFilters = {}) =>
  transport.request(
    '/ops/audit-events',
    object({
      items: arrayOf(
        object({
          id: str,
          actor_user_id: str,
          actor_email: optional(nullableStr, null),
          tenant_id: nullableStr,
          tenant_name: optional(nullableStr, null),
          action: str,
          outcome: str,
          details: record(anyBody),
          request_id: nullableStr,
          created_at: str,
        }),
      ),
      total: optional(int, 0),
    }),
    { withTenant: false, query: { offset, ...filters }, ...(signal ? { signal } : {}) },
  );
export const fetchPlatformSources = (signal?: AbortSignal) =>
  transport.request(
    '/ops/public-sources',
    arrayOf((value, context) =>
      sourceDecoder({ ...(value as Record<string, unknown>), subscribed: false }, context),
    ),
    {
      withTenant: false,
      ...(signal ? { signal } : {}),
    },
  );
